import {useMutation, useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import {Avatar} from 'primereact/avatar'
import {Button} from 'primereact/button'
import {Carousel} from 'primereact/carousel'
import {Image} from 'primereact/image'
import {Sidebar} from 'primereact/sidebar'
import {Tag} from 'primereact/tag'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import DoctorIcon from '../../assets/icons/Doctor.svg'
import chatImg from '../../assets/images/Conversation.jpg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId, setIsOpenChat} from '../authentication/AuthUtils'
import {chatEvent} from '../chat/ChatService'
import Room from '../chat/Room'
import ExercisesChart from '../exercise/ExercisesChart'
import FormHeader from '../form/FormHeader'
import HealthRecordChart from '../health-record-chart/HealthRecordChart'
import PageContentWrapper from '../layout/PageContentWrapper'
import StyledTitle from '../layout/StyledTitle'
import {observationKeys} from '../observations/Constants'
import {useNewestHealthRecords} from '../observations/query-keys/ObsevationKeysManager'
import {convertSkinTempToMBT, isArrayAndNotEmpty} from '../observations/Utils'
import {
    getHealthCareEventKey,
    healthCareEvent,
    healthCareEventTypes,
} from '../styled-component/hc-practitioner/broadcast/HealthCarePractitionerEvent'
import {
    getPatientEventKey,
    patientEvent,
    patientEventTypes,
} from '../styled-component/patient/broastcast/PatientEvent'
import {useListSupportTeams} from '../support-teams/query-keys/SupportTeamKeysManager'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {useListUserManagement} from '../user-property/query-keys/UserPropertyQueryManager'
import {UserPropertyType} from '../user-property/UserPropertyConstants'
import {ReactComponent as ChartIcon} from './asset/Chart.svg'
import {ReactComponent as HistoryIcon} from './asset/History.svg'
import PatientObservationForm from './PatientObservationForm'
import {latestObsList} from './PatientPageConstants'
import PatientSymptomForm from './PatientSymptom'

const QueryHealthRecord = 'Health-Record'

export const AccessConfigKeys = {
    watchInteraction: 'watchInteraction',
    allowToAccessPatientPage: 'allowToAccessPatientPage',
    allowToReportSymptom: 'allowToReportSymptom',
    allowToAddObservation: 'allowToAddObservation',
    allowToChatWithDoctors: 'allowToChatWithDoctors',
    allowToViewActivities: 'allowToViewActivities',
    allowToViewNewestObservations: 'allowToViewNewestObservations',
}
const itemType = {
    hcp: 'hcp',
    support: 'support',
}
const PatientDashboard = () => {
    const {user} = useAuth()
    const {translateCommons} = TranslateUtils()
    const [visible, setVisible] = useState(false)
    const obsForm = useRef(null)
    const symptom = useRef(null)

    //#region Query
    const queryClient = useQueryClient()

    const {data: hcpData} = useListUserManagement(
        user?.id,
        true,
        {subRefId: user?.id},
        !!user?.id,
    )
    const {data: ownedSupportTeams} = useListSupportTeams(
        user?.hospitalId || null,
        user?.id,
        false,
        {userIdInParticipant: user?.id, hospitalId: user?.hospitalId || null},
        !!user?.id,
    )

    const {data: obsData} = useNewestHealthRecords(
        user?.id,
        user?.id,
        !!user?.id,
    )

    const {mutate: handleReloadHealthRecord} = useMutation(data => data, {
        onSuccess: data => {
            if (!!data?.id) {
                queryClient.setQueryData(
                    [QueryHealthRecord, user?.id],
                    () => data,
                )
            }
        },
    })

    const handleReloadHealthRecordData = useCallback(
        data => {
            if (!!data?.id) {
                handleReloadHealthRecord(data)
            }
        },
        [handleReloadHealthRecord],
    )
    //#endregion

    //#region Render
    const haveAccessPermission = accessPermissionKey => {
        const accessConfig = user?.accessConfig

        if (!accessConfig || Object.keys(accessConfig)?.length < 1) {
            return false
        }

        return !!accessConfig[accessPermissionKey]
    }

    const generateGreetings = useMemo(() => {
        let currentHour = moment().format('HH')

        if (currentHour >= 5 && currentHour < 12) {
            return 'Good morning'
        } else if (currentHour >= 12 && currentHour < 18) {
            return 'Good afternoon'
        } else if (currentHour >= 18 && currentHour < 24) {
            return 'Good evening'
        } else if (currentHour >= 24 || currentHour < 5) {
            return 'Good night'
        } else {
            return 'Hello'
        }
    }, [])

    /* #Carousel*/
    const responsiveOptions = useMemo(() => {
        return [
            {
                breakpoint: '1400px',
                numVisible: 2,
                numScroll: 1,
            },
            {
                breakpoint: '1199px',
                numVisible: 2,
                numScroll: 1,
            },
            {
                breakpoint: '767px',
                numVisible: 1,
                numScroll: 1,
            },
            {
                breakpoint: '575px',
                numVisible: 1,
                numScroll: 1,
            },
        ]
    }, [])

    const hcpItemTemplate = item => {}
    const supportTeamItemTemplate = item => {
        return (
            <div key={item.id}>
                {isArrayAndNotEmpty(item.selectedParticipants) ? (
                    <div className="m-2 text-center py-6 px-2 bg-gray-50">
                        <div className="flex justify-content-center  mb-3 ">
                            <div className="relative">
                                {' '}
                                <Avatar
                                    className="border-circle bg-white"
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                    }}
                                    image={
                                        !!item?.mainRefAvatar
                                            ? item?.mainRefAvatar
                                            : DoctorIcon
                                    }
                                    size="large"
                                    shape="circle"
                                />
                                <div
                                    className="absolute"
                                    style={{top: '10px', right: '-50px'}}>
                                    <Tag className=" bg-blue-500 ">
                                        {' '}
                                        Support Team
                                    </Tag>
                                </div>
                            </div>
                        </div>
                        <div>
                            <h4 className="mb-1">
                                {!!item?.mainRefAvatar
                                    ? item?.mainRefAvatar
                                    : DoctorIcon}
                            </h4>
                            <h6 className="mt-0 mb-2">
                                {' '}
                                {!!item.hospitalName
                                    ? item.hospitalName
                                    : '---'}
                            </h6>
                            <h6>
                                Number Participants: {item.numberOfParticipants}
                            </h6>
                        </div>
                    </div>
                ) : (
                    []
                )}
            </div>
        )
    }
    //#endregion

    //#region Data
    const participantsData = useMemo(() => {
        const hcpItems = isArrayAndNotEmpty(hcpData)
            ? hcpData.map(item => ({type: itemType.hcp, data: item}))
            : []

        const supportItems = isArrayAndNotEmpty(ownedSupportTeams)
            ? ownedSupportTeams.map(item => ({
                  type: itemType.support,
                  data: item,
              }))
            : []

        return [...hcpItems, ...supportItems]
    }, [hcpData, ownedSupportTeams])

    const participantsTemplate = item => {
        if (![itemType.hcp, itemType.support].includes(item.type)) {
            return null
        }

        let avatar =
            itemType.hcp === item.type && item.data?.mainRefAvatar
                ? item.data.mainRefAvatar
                : DoctorIcon
        let tag = ''
        let name = ''
        let hospital = ''
        let email = ''

        const itemData = item.data

        if (item.type === itemType.hcp) {
            const isGp = !!itemData?.gpId
            tag = isGp ? 'General Practitioner' : itemData.roleName || ''
            name = isGp ? itemData.gpName : itemData.mainRefName
            email = isGp ? itemData.gpEmail : itemData.mainRefEmail
            hospital = isGp ? null : itemData.hospitalName
        } else if (item.type === itemType.support) {
            tag = 'Support Team'
            name = itemData.name
            email = itemData.email
            hospital = itemData.hospitalName
        }

        return (
            <div
                key={item.type + '-' + itemData?.id}
                className="m-2 text-center py-6 px-2 bg-gray-50 rounded-2xl shadow-1 h-full ">
                <div className="flex justify-content-center mb-3">
                    <div className="relative">
                        <Avatar
                            className="border-circle bg-white"
                            style={{width: '100px', height: '100px'}}
                            image={avatar}
                            size="large"
                            shape="circle"
                        />
                        {tag && (
                            <div
                                className="absolute"
                                style={{
                                    top: '10px',
                                    right:
                                        item.type === itemType.support
                                            ? '-50px'
                                            : '-20px',
                                }}>
                                <Tag className="bg-blue-500">{tag}</Tag>
                            </div>
                        )}
                    </div>
                </div>
                <div className="border-round-xl p-1">
                    <h4 className="mb-1">{name}</h4>
                    {hospital && <h6 className="mt-0 mb-2">{hospital}</h6>}
                    {email && (
                        <div className="flex flex-wrap gap-2 justify-content-center">
                            Email: {email}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    //#endregion

    //#region Event Listener on State Changes
    useEffect(() => {
        // HCP'S MANAGEMENT CHANGE
        healthCareEvent.on(
            getHealthCareEventKey(healthCareEventTypes.USER_MANAGEMENT_CHANGE),
            data => {
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (
                    !data?.subRefIds ||
                    !data?.subRefIds?.length ||
                    !data?.subRefIds?.includes(user?.id)
                ) {
                    return
                }

                const {userPropertyKeys} = QueryKeyManager(
                    QueryKeyTypes.userProperty,
                    user?.id,
                )

                queryClient.invalidateQueries(
                    userPropertyKeys.pattern({
                        type: UserPropertyType.userManagement,
                    }),
                )
            },
        )

        // SUPPORT TEAM CHANGE
        patientEvent.on(
            getPatientEventKey(patientEventTypes.SUPPORT_TEAM_CHANGE),
            data => {
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (
                    !data?.hospitalId ||
                    data?.hospitalId !== user?.hospitalId
                ) {
                    return
                }

                if (!data?.userId || data?.userId !== user?.id) {
                    return
                }

                const {supportTeamKeys} = QueryKeyManager(
                    QueryKeyTypes.supportTeam,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: supportTeamKeys.pattern(),
                })
            },
        )
    }, [])
    //#endregion

    return (
        <PageContentWrapper>
            <div className="grid p-0">
                <div className="col-12 mb-4">
                    <div className="surface-card border-round-xl shadow-3 p-5 bg-gradient-to-br from-blue-50 to-teal-50">
                        <div className="">
                            <h1 className="font-bold text-4xl m-0 mb-3 text-black bg-clip-text">
                                {translateCommons(generateGreetings)},{' '}
                                {user?.fullName}! 👋
                            </h1>
                            <div className="flex align-items-center justify-content-center gap-1 bg-white px-4 py-3 border-round-lg shadow-1 inline-flex">
                                <div className="flex align-items-center justify-content-center w-2rem h-2rem  border-circle">
                                    <i className="pi pi-calendar text-black text-md font-bold"></i>
                                </div>
                                <span className="font-semibold text-lg text-700">
                                    {moment().format('dddd, MMMM Do YYYY')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 m-0 p-0">
                    <div className="grid p-0">
                        {/* #Hello Option*/}
                        {haveAccessPermission(
                            AccessConfigKeys.allowToAddObservation,
                        ) && (
                            <div className="col-12 md:col">
                                <div className="flex h-full flex-column align-items-center justify-content-between surface-card border-round shadow-2 p-2 text-center">
                                    <div>
                                        {' '}
                                        <Image
                                            height="200px"
                                            src={require('./../../assets/images/Observation.jpg')}
                                        />
                                        <h3 className="text-black font-bold mt-2 mb-2  ">
                                            {translateCommons('Observation')}
                                        </h3>
                                    </div>

                                    <p className="text-md lg:text-lg mt-0 mb-4 p-0 line-height-3  col-8 text-center">
                                        {translateCommons(
                                            `You can add your vital signs here, along with any concerns or changes you'd like your doctors to know about.`,
                                        )}
                                    </p>
                                    <Button
                                        label={
                                            <label className="px-3">
                                                {translateCommons(
                                                    'Add Observation',
                                                )}
                                            </label>
                                        }
                                        icon="pi pi-chart-line"
                                        onClick={() =>
                                            obsForm.current?.showForm()
                                        }
                                    />
                                </div>
                            </div>
                        )}

                        {haveAccessPermission(
                            AccessConfigKeys.allowToReportSymptom,
                        ) && (
                            <div className="col-12 md:col">
                                <div className="flex h-full flex-column align-items-center justify-content-between surface-card border-round shadow-2 p-2 text-center">
                                    <div>
                                        {' '}
                                        <Image
                                            height="200px"
                                            src={require('./../../assets/images/Symptom.jpg')}
                                        />
                                        <h3 className="text-black font-bold mt-2 mb-2  ">
                                            {translateCommons('Report Symptom')}
                                        </h3>
                                    </div>

                                    <p className="text-md lg:text-lg mt-0 mb-4 p-0 line-height-3 col-8 text-center">
                                        {/* {translateCommons(
                                            "Here you can report any symptoms you've been experiencing, any concerns or new changes that you wish your doctors to know about.",
                                        )} */}
                                        {translateCommons(
                                            "You can report any symptoms here, along with any concerns or changes you'd like your doctors to know about.",
                                        )}
                                    </p>
                                    <Button
                                        label={
                                            <label className="px-3">
                                                {translateCommons(
                                                    'Add Symptom',
                                                )}
                                            </label>
                                        }
                                        icon="pi pi-calendar-plus"
                                        onClick={() =>
                                            symptom.current?.showForm()
                                        }
                                    />
                                </div>
                            </div>
                        )}

                        {haveAccessPermission(
                            AccessConfigKeys.allowToChatWithDoctors,
                        ) && (
                            <div className="col-12 md:col">
                                <div className="flex h-full flex-column align-items-center justify-content-between surface-card border-round shadow-2 p-2 text-center">
                                    <div>
                                        <Image height="200px" src={chatImg} />
                                        <h3 className="text-black font-bold mt-2 mb-2  ">
                                            {translateCommons('Conversation')}
                                        </h3>
                                    </div>
                                    <p className="text-md lg:text-lg mt-0 mb-4 p-0 line-height-3  col-8 text-center">
                                        {translateCommons(
                                            'You can chat live with your doctors here. Your messages are secure as per our privacy policy.',
                                        )}
                                        {/* <br />
                                        {translateCommons(
                                            'Your messages will be secure in term of our privacy policy.',
                                        )} */}
                                    </p>
                                    <Button
                                        icon="pi pi-comments"
                                        label={
                                            <label className="px-3">
                                                {translateCommons(
                                                    'Chat With Doctors',
                                                )}
                                            </label>
                                        }
                                        onClick={() => {
                                            blockOverflownScroll()
                                            setVisible(true)
                                            setIsOpenChat(true)
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* #endregion */}

                        {/* #Doctor */}
                        <div className="col-12">
                            <div
                                className="p-0 mb-3"
                                style={{
                                    borderRadius: '20px',
                                }}>
                                <div className="">
                                    <StyledTitle
                                        title={translateCommons(
                                            'Health Care Practitioners',
                                        )}
                                    />
                                </div>
                                {Array.isArray(participantsData) &&
                                participantsData.length > 1 ? (
                                    <div className="p-0">
                                        <Carousel
                                            value={participantsData}
                                            numScroll={1}
                                            numVisible={3}
                                            circular={true}
                                            showNavigators={true}
                                            showIndicators={true}
                                            autoplayInterval={3000}
                                            responsiveOptions={
                                                responsiveOptions
                                            }
                                            indicatorsContentClassName="mb-5 p-0 custom-carousel"
                                            contentClassName="m-0 p-0"
                                            itemTemplate={participantsTemplate}
                                        />
                                    </div>
                                ) : Array.isArray(participantsData) &&
                                  participantsData.length === 1 ? (
                                    <div className="text-center">
                                        {participantsData &&
                                            participantsData?.length > 0 &&
                                            participantsData.map((item, idx) =>
                                                participantsTemplate(item),
                                            )}
                                    </div>
                                ) : (
                                    <div className="py-2 px-2">
                                        <h6
                                            className={`text-600 font-italic text-center`}>
                                            {translateCommons(
                                                'No Health Care Practitioners',
                                            )}
                                        </h6>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* #endregion */}
                        {/* Chart */}
                        {haveAccessPermission(
                            AccessConfigKeys.allowToViewActivities,
                        ) && (
                            <div
                                className={`${
                                    haveAccessPermission(
                                        AccessConfigKeys.allowToViewActivities,
                                    )
                                        ? 'col-12 lg:col-6'
                                        : 'col-12'
                                }`}>
                                {' '}
                                <div className="">
                                    <StyledTitle
                                        title={translateCommons('Statistic')}
                                        icon={<ChartIcon className="" />}
                                    />
                                    <span>
                                        Track daily activities like steps,
                                        sleep, calories, heart rate, SpO₂, and
                                        temperature with clear charts. Sync
                                        anytime to keep your health data up to
                                        date.
                                    </span>
                                </div>
                                <ExercisesChart hideToggleButton={true} />
                            </div>
                        )}

                        {haveAccessPermission(
                            AccessConfigKeys.allowToViewNewestObservations,
                        ) &&
                            obsData &&
                            latestObsList && (
                                <div className="lg:col-6 px-3 col-12">
                                    <div className="border-round-xl shadow-none p-0  transition-all transition-duration-300">
                                        <div className="mb-5">
                                            <div className="ml-6">
                                                <StyledTitle
                                                    title={translateCommons(
                                                        'Last Observation',
                                                    )}
                                                    icon={
                                                        <HistoryIcon className="" />
                                                    }
                                                />
                                                <span>
                                                    View your latest vital signs
                                                    at a glance—heart rate,
                                                    respiration, oxygen,
                                                    temperature, blood pressure,
                                                    glucose, and consciousness
                                                    level—with instant alerts
                                                    for any changes.
                                                </span>
                                            </div>
                                        </div>

                                        <div className="bg-white border-round-lg p-0">
                                            <ul
                                                className="list-none px-6 m-0"
                                                style={{minHeight: '30vh'}}>
                                                {obsData &&
                                                    latestObsList.map(
                                                        (item, index) => (
                                                            <li
                                                                key={index}
                                                                className="flex align-items-center py-3 px-3 mb-1 bg-gray-50 border-round-lg transition-all transition-duration-200 hover:bg-gray-100 hover:shadow-1">
                                                                <div className="flex align-items-center justify-content-center w-2rem h-2rem border-gray-200 mr-2 flex-shrink-0">
                                                                    <div className="flex align-items-center justify-content-center w-2rem h-2rem border-round border-gray-200 flex-shrink-0 mr-2">
                                                                        {item.icon()}
                                                                    </div>
                                                                </div>

                                                                <span className="text-lg font-medium text-600 flex-1">
                                                                    {translateCommons(
                                                                        convertKeyToLabel(
                                                                            item.key,
                                                                        ),
                                                                    )}
                                                                </span>

                                                                <div className="flex align-items-center gap-2">
                                                                    <span className="text-gray-900 text-lg font-bold bg-none px-3 py-1 border-round-lg">
                                                                        {`${
                                                                            obsData[
                                                                                item
                                                                                    .key
                                                                            ]
                                                                                ? !!obsData[
                                                                                      item
                                                                                          .key
                                                                                  ] &&
                                                                                  isNaN(
                                                                                      obsData[
                                                                                          item
                                                                                              .key
                                                                                      ],
                                                                                  )
                                                                                    ? convertKeyToLabel(
                                                                                          obsData[
                                                                                              item
                                                                                                  .key
                                                                                          ],
                                                                                      )
                                                                                    : item.key ===
                                                                                      observationKeys.temperature
                                                                                    ? convertSkinTempToMBT(
                                                                                          obsData[
                                                                                              item
                                                                                                  .key
                                                                                          ],
                                                                                      )
                                                                                    : obsData[
                                                                                          item
                                                                                              .key
                                                                                      ]
                                                                                : '-'
                                                                        } ${
                                                                            item.shorten &&
                                                                            obsData[
                                                                                item
                                                                                    .key
                                                                            ]
                                                                                ? item.shorten
                                                                                : ''
                                                                        }`}
                                                                    </span>
                                                                </div>
                                                            </li>
                                                        ),
                                                    )}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                    </div>
                </div>
                <div className="col-12" style={{height: '800px'}}>
                    <HealthRecordChart />
                </div>
                {/* #Chart*/}
                <div className="col-12 m-0 p-0">
                    <div className="grid p-0">
                        <PatientObservationForm
                            ref={obsForm}
                            reload={data => handleReloadHealthRecordData(data)}
                        />
                        <PatientSymptomForm ref={symptom} />
                        <Sidebar
                            className="p-sidebar-full "
                            visible={visible}
                            blockScroll
                            onHide={() => {
                                setVisible(false)
                                unblockOverflownScroll()
                            }}
                            fullScreen>
                            <PageContentWrapper>
                                <FormHeader
                                    header={translateCommons('CONVERSATION')}
                                    onHide={() => {
                                        chatEvent.emit('MENU-CHANGE')
                                        setVisible(false)
                                        setIsOpenChat(false)
                                        unblockOverflownScroll()
                                    }}
                                />
                                <Room hcpData={hcpData} />
                            </PageContentWrapper>
                        </Sidebar>
                    </div>
                </div>
                {/* #endregion */}
            </div>
        </PageContentWrapper>
    )
}

export default PatientDashboard
